import { useMutation, useQuery } from '@apollo/client';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { Button } from 'components/Button/Button';
import { Card, CardContent, CardHeader } from 'components/Card/Card';
import { Icon } from 'components/Icon/Icon';
import { Input } from 'components/Input/Input';
import { PageTitle } from 'components/PageTitle/PageTitle';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { COMPANIES } from 'services/companies/query';
import {
    INVOICE,
    INVOICE_CREATE,
    INVOICE_UPDATE,
    INVOICE_LINE_CREATE,
    INVOICE_LINE_UPDATE,
    INVOICE_LINE_DELETE,
    INVOICE_SUBMIT_FOR_APPROVAL,
} from 'services/invoices';
import { USERS_FULL } from 'services/users/query';

interface InvoiceLine {
    id?: string;
    description: string;
    quantity: number;
    unitPrice: number;
    discountPercent: number;
    discountAmount: number;
    taxRate: number;
    sortOrder: number;
    isNew?: boolean;
    isModified?: boolean;
    isDeleted?: boolean;
}

const calculateLineTotal = (line: InvoiceLine) => {
    const base = line.quantity * line.unitPrice;
    const discountValue = line.discountPercent > 0 ? base * (line.discountPercent / 100) : line.discountAmount;
    return base - discountValue;
};

const calculateLineTax = (line: InvoiceLine) => {
    const total = calculateLineTotal(line);
    return total * (line.taxRate / 100);
};

export const InvoiceEditor = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t: b, t } = useTranslation('translation');
    const isEditing = Boolean(id);
    
    // Translated type options
    const TYPE_OPTIONS_TRANSLATED = [
        { value: 'CUSTOM', label: t('invoices.typeCustom') },
        { value: 'MEMBERSHIP', label: t('invoices.typeMembership') },
        { value: 'CONSUMPTION', label: t('invoices.typeConsumption') },
        { value: 'NEGATIVE_BALANCE', label: t('invoices.typeNegativeBalance') },
        { value: 'TOP_UP', label: t('invoices.typeTopUp') },
    ];
    
    // Get pre-filled IDs from URL params
    const prefilledMemberId = searchParams.get('memberId');
    const prefilledCompanyId = searchParams.get('companyId');

    // Form state
    const [recipientType, setRecipientType] = useState<'member' | 'company'>(prefilledCompanyId ? 'company' : 'member');
    const [selectedMember, setSelectedMember] = useState<any>(null);
    const [selectedCompany, setSelectedCompany] = useState<any>(null);
    const [invoiceType, setInvoiceType] = useState('CUSTOM');
    const [dueDate, setDueDate] = useState(dayjs().add(20, 'day').format('YYYY-MM-DD'));
    const [headerNotes, setHeaderNotes] = useState('');
    const [footerNotes, setFooterNotes] = useState('');
    const [internalNotes, setInternalNotes] = useState('');
    const [discountType, setDiscountType] = useState<'percent' | 'amount'>('percent');
    const [discountValue, setDiscountValue] = useState(0);
    const [discountDescription, setDiscountDescription] = useState('');
    const [lines, setLines] = useState<InvoiceLine[]>([]);
    const [saving, setSaving] = useState(false);

    // Queries
    const { data: invoiceData, loading: loadingInvoice } = useQuery(INVOICE, {
        variables: { id },
        skip: !id,
    });

    const { data: membersData } = useQuery(USERS_FULL, {
        variables: { isMember: true, first: 100 },
    });

    const { data: companiesData } = useQuery(COMPANIES, {
        variables: { first: 100 },
    });

    // Mutations
    const [createInvoice] = useMutation(INVOICE_CREATE);
    const [updateInvoice] = useMutation(INVOICE_UPDATE);
    const [createLine] = useMutation(INVOICE_LINE_CREATE);
    const [updateLine] = useMutation(INVOICE_LINE_UPDATE);
    const [deleteLine] = useMutation(INVOICE_LINE_DELETE);
    const [submitForApproval] = useMutation(INVOICE_SUBMIT_FOR_APPROVAL);

    // Load existing invoice data
    useEffect(() => {
        if (invoiceData?.invoice) {
            const inv = invoiceData.invoice;
            setInvoiceType(inv.type);
            setDueDate(inv.dueDate);
            setHeaderNotes(inv.headerNotes || '');
            setFooterNotes(inv.footerNotes || '');
            setInternalNotes(inv.internalNotes || '');

            if (inv.discountPercent > 0) {
                setDiscountType('percent');
                setDiscountValue(parseFloat(inv.discountPercent));
            } else if (inv.discountAmount > 0) {
                setDiscountType('amount');
                setDiscountValue(parseFloat(inv.discountAmount));
            }
            setDiscountDescription(inv.discountDescription || '');

            if (inv.member) {
                setRecipientType('member');
                setSelectedMember({ value: inv.member.id, label: `${inv.member.firstName} ${inv.member.lastName}` });
            } else if (inv.company) {
                setRecipientType('company');
                setSelectedCompany({ value: inv.company.id, label: inv.company.name });
            }

            if (inv.lines) {
                setLines(
                    inv.lines.map((line: any) => ({
                        id: line.id,
                        description: line.description,
                        quantity: parseFloat(line.quantity),
                        unitPrice: parseFloat(line.unitPrice),
                        discountPercent: parseFloat(line.discountPercent),
                        discountAmount: parseFloat(line.discountAmount),
                        taxRate: parseFloat(line.taxRate),
                        sortOrder: line.sortOrder,
                    }))
                );
            }
        }
    }, [invoiceData]);

    const memberOptions = useMemo(
        () =>
            membersData?.users?.edges?.map((edge: any) => ({
                value: edge.node.id,
                label: `${edge.node.firstName} ${edge.node.lastName} (${edge.node.email})`,
            })) || [],
        [membersData]
    );

    const companyOptions = useMemo(
        () =>
            companiesData?.companies?.edges?.map((edge: any) => ({
                value: edge.node.id,
                label: edge.node.name,
            })) || [],
        [companiesData]
    );

    // Pre-select member/company from URL params
    useEffect(() => {
        if (prefilledMemberId && memberOptions.length > 0 && !selectedMember) {
            const member = memberOptions.find((m: any) => m.value === prefilledMemberId);
            if (member) {
                setSelectedMember(member);
            }
        }
    }, [prefilledMemberId, memberOptions, selectedMember]);

    useEffect(() => {
        if (prefilledCompanyId && companyOptions.length > 0 && !selectedCompany) {
            const company = companyOptions.find((c: any) => c.value === prefilledCompanyId);
            if (company) {
                setSelectedCompany(company);
            }
        }
    }, [prefilledCompanyId, companyOptions, selectedCompany]);

    const addLine = () => {
        setLines([
            ...lines,
            {
                description: '',
                quantity: 1,
                unitPrice: 0,
                discountPercent: 0,
                discountAmount: 0,
                taxRate: 7.7,
                sortOrder: lines.length,
                isNew: true,
            },
        ]);
    };

    const updateLineField = (index: number, field: keyof InvoiceLine, value: any) => {
        const updated = [...lines];
        updated[index] = {
            ...updated[index],
            [field]: value,
            isModified: !updated[index].isNew,
        };
        setLines(updated);
    };

    const removeLine = (index: number) => {
        const updated = [...lines];
        if (updated[index].id) {
            // Mark for deletion
            updated[index].isDeleted = true;
        } else {
            // Just remove from array
            updated.splice(index, 1);
        }
        setLines(updated);
    };

    const calculateTotals = () => {
        const activelines = lines.filter((l) => !l.isDeleted);
        const subtotal = activelines.reduce((sum, line) => sum + calculateLineTotal(line), 0);
        const taxTotal = activelines.reduce((sum, line) => sum + calculateLineTax(line), 0);
        const discountCalc = discountType === 'percent' ? subtotal * (discountValue / 100) : discountValue;
        const total = subtotal - discountCalc + taxTotal;
        return { subtotal, taxTotal, discount: discountCalc, total };
    };

    const handleSave = async (andSubmit = false) => {
        setSaving(true);
        try {
            let invoiceId = id;

            // Create or update invoice
            if (!isEditing) {
                const result = await createInvoice({
                    variables: {
                        memberId: recipientType === 'member' ? selectedMember?.value : undefined,
                        companyId: recipientType === 'company' ? selectedCompany?.value : undefined,
                        type: invoiceType,
                        dueDate,
                        headerNotes,
                        footerNotes,
                        internalNotes,
                    },
                });
                invoiceId = result.data.invoiceCreate.invoice.id;
            } else {
                await updateInvoice({
                    variables: {
                        id: invoiceId,
                        dueDate,
                        headerNotes,
                        footerNotes,
                        internalNotes,
                        discountPercent: discountType === 'percent' ? discountValue : 0,
                        discountAmount: discountType === 'amount' ? discountValue : 0,
                        discountDescription,
                    },
                });
            }

            // Handle lines
            for (const line of lines) {
                if (line.isDeleted && line.id) {
                    await deleteLine({ variables: { id: line.id } });
                } else if (line.isNew && !line.isDeleted) {
                    await createLine({
                        variables: {
                            invoiceId,
                            description: line.description,
                            quantity: line.quantity,
                            unitPrice: line.unitPrice,
                            discountPercent: line.discountPercent,
                            discountAmount: line.discountAmount,
                            taxRate: line.taxRate,
                            sortOrder: line.sortOrder,
                        },
                    });
                } else if (line.isModified && line.id) {
                    await updateLine({
                        variables: {
                            id: line.id,
                            description: line.description,
                            quantity: line.quantity,
                            unitPrice: line.unitPrice,
                            discountPercent: line.discountPercent,
                            discountAmount: line.discountAmount,
                            taxRate: line.taxRate,
                            sortOrder: line.sortOrder,
                        },
                    });
                }
            }

            if (andSubmit && invoiceId) {
                await submitForApproval({ variables: { id: invoiceId } });
            }

            navigate(`/invoices/${invoiceId}`);
        } catch (error) {
            console.error('Failed to save invoice:', error);
            alert('Failed to save invoice. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const totals = calculateTotals();

    if (loadingInvoice) {
        return <div className="p-8 text-center">{t('invoices.loading')}</div>;
    }

    return (
        <div className="pb-8">
            <Breadcrumbs
                crumbs={[b('breadcrumbs.dashboard'), t('invoices.title'), isEditing ? t('invoices.editInvoice') : t('invoices.newInvoice')]}
            />
            <PageTitle withBack>{isEditing ? t('invoices.editInvoice') : t('invoices.createNewInvoice')}</PageTitle>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Recipient Selection */}
                    <Card>
                        <CardHeader>
                            <h3 className="text-lg font-semibold">{t('invoices.recipient')}</h3>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="flex gap-4">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            checked={recipientType === 'member'}
                                            onChange={() => setRecipientType('member')}
                                            disabled={isEditing}
                                        />
                                        {t('invoices.member')}
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            checked={recipientType === 'company'}
                                            onChange={() => setRecipientType('company')}
                                            disabled={isEditing}
                                        />
                                        {t('invoices.company')}
                                    </label>
                                </div>

                                {recipientType === 'member' ? (
                                    <Select
                                        value={selectedMember}
                                        onChange={setSelectedMember}
                                        options={memberOptions}
                                        placeholder={t('invoices.selectMember')}
                                        isDisabled={isEditing}
                                        isClearable
                                    />
                                ) : (
                                    <Select
                                        value={selectedCompany}
                                        onChange={setSelectedCompany}
                                        options={companyOptions}
                                        placeholder={t('invoices.selectCompany')}
                                        isDisabled={isEditing}
                                        isClearable
                                    />
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Invoice Details */}
                    <Card>
                        <CardHeader>
                            <h3 className="text-lg font-semibold">{t('invoices.invoiceDetails')}</h3>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t('invoices.type')}</label>
                                    <select
                                        value={invoiceType}
                                        onChange={(e) => setInvoiceType(e.target.value)}
                                        className="w-full border rounded-md px-3 py-2"
                                        disabled={isEditing}
                                    >
                                        {TYPE_OPTIONS_TRANSLATED.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t('invoices.dueDate')}</label>
                                    <Input
                                        type="date"
                                        value={dueDate}
                                        onChange={(e) => setDueDate(e.target.value)}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Line Items */}
                    <Card>
                        <CardHeader>
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-semibold">{t('invoices.lineItems')}</h3>
                                <Button onClick={addLine} variant="outline" size="sm">
                                    {t('invoices.addLine')}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {lines.filter((l) => !l.isDeleted).length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        {t('invoices.noLineItems')}
                                    </div>
                                ) : (
                                    lines
                                        .filter((l) => !l.isDeleted)
                                        .map((line, index) => (
                                            <div
                                                key={line.id || `new-${index}`}
                                                className="border rounded-lg p-4 bg-gray-50"
                                            >
                                                <div className="grid grid-cols-12 gap-4">
                                                    <div className="col-span-12 md:col-span-5">
                                                        <label className="block text-xs text-gray-500 mb-1">
                                                            {t('invoices.description')}
                                                        </label>
                                                        <Input
                                                            value={line.description}
                                                            onChange={(e) =>
                                                                updateLineField(index, 'description', e.target.value)
                                                            }
                                                            placeholder={t('invoices.itemDescription')}
                                                        />
                                                    </div>
                                                    <div className="col-span-4 md:col-span-1">
                                                        <label className="block text-xs text-gray-500 mb-1">{t('invoices.quantity')}</label>
                                                        <Input
                                                            type="number"
                                                            value={line.quantity}
                                                            onChange={(e) =>
                                                                updateLineField(
                                                                    index,
                                                                    'quantity',
                                                                    parseFloat(e.target.value) || 0
                                                                )
                                                            }
                                                            min={1}
                                                        />
                                                    </div>
                                                    <div className="col-span-4 md:col-span-2">
                                                        <label className="block text-xs text-gray-500 mb-1">
                                                            {t('invoices.unitPrice')}
                                                        </label>
                                                        <Input
                                                            type="number"
                                                            value={line.unitPrice}
                                                            onChange={(e) =>
                                                                updateLineField(
                                                                    index,
                                                                    'unitPrice',
                                                                    parseFloat(e.target.value) || 0
                                                                )
                                                            }
                                                            step="0.01"
                                                        />
                                                    </div>
                                                    <div className="col-span-4 md:col-span-1">
                                                        <label className="block text-xs text-gray-500 mb-1">
                                                            {t('invoices.taxPercent')}
                                                        </label>
                                                        <Input
                                                            type="number"
                                                            value={line.taxRate}
                                                            onChange={(e) =>
                                                                updateLineField(
                                                                    index,
                                                                    'taxRate',
                                                                    parseFloat(e.target.value) || 0
                                                                )
                                                            }
                                                            step="0.1"
                                                        />
                                                    </div>
                                                    <div className="col-span-6 md:col-span-2">
                                                        <label className="block text-xs text-gray-500 mb-1">
                                                            {t('invoices.discountPercent')}
                                                        </label>
                                                        <Input
                                                            type="number"
                                                            value={line.discountPercent}
                                                            onChange={(e) =>
                                                                updateLineField(
                                                                    index,
                                                                    'discountPercent',
                                                                    parseFloat(e.target.value) || 0
                                                                )
                                                            }
                                                            step="0.1"
                                                        />
                                                    </div>
                                                    <div className="col-span-6 md:col-span-1 flex items-end">
                                                        <Button
                                                            variant="destructive"
                                                            size="sm"
                                                            onClick={() => removeLine(index)}
                                                        >
                                                            <Icon name="trash" className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                                <div className="mt-2 text-right text-sm text-gray-600">
                                                    {t('invoices.lineTotal')}{' '}
                                                    <span className="font-medium">
                                                        CHF {calculateLineTotal(line).toFixed(2)}
                                                    </span>
                                                    <span className="text-gray-400 ml-2">
                                                        (+ CHF {calculateLineTax(line).toFixed(2)} {t('invoices.tax')})
                                                    </span>
                                                </div>
                                            </div>
                                        ))
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Notes */}
                    <Card>
                        <CardHeader>
                            <h3 className="text-lg font-semibold">{t('invoices.notes')}</h3>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t('invoices.headerNotes')}</label>
                                    <textarea
                                        value={headerNotes}
                                        onChange={(e) => setHeaderNotes(e.target.value)}
                                        rows={2}
                                        className="w-full border rounded-md px-3 py-2"
                                        placeholder={t('invoices.headerNotesPlaceholder')}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t('invoices.footerNotes')}</label>
                                    <textarea
                                        value={footerNotes}
                                        onChange={(e) => setFooterNotes(e.target.value)}
                                        rows={2}
                                        className="w-full border rounded-md px-3 py-2"
                                        placeholder={t('invoices.footerNotesPlaceholder')}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">
                                        {t('invoices.internalNotes')}
                                    </label>
                                    <textarea
                                        value={internalNotes}
                                        onChange={(e) => setInternalNotes(e.target.value)}
                                        rows={2}
                                        className="w-full border rounded-md px-3 py-2"
                                        placeholder={t('invoices.internalNotesPlaceholder')}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Sidebar - Totals & Actions */}
                <div className="space-y-6">
                    {/* Invoice-level Discount */}
                    <Card>
                        <CardHeader>
                            <h3 className="text-lg font-semibold">{t('invoices.invoiceDiscount')}</h3>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="flex gap-4">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            checked={discountType === 'percent'}
                                            onChange={() => setDiscountType('percent')}
                                        />
                                        {t('invoices.percentage')}
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            checked={discountType === 'amount'}
                                            onChange={() => setDiscountType('amount')}
                                        />
                                        {t('invoices.fixedAmount')}
                                    </label>
                                </div>
                                <Input
                                    type="number"
                                    value={discountValue}
                                    onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                                    placeholder={discountType === 'percent' ? t('invoices.discountPercentPlaceholder') : t('invoices.discountAmountPlaceholder')}
                                />
                                <Input
                                    value={discountDescription}
                                    onChange={(e) => setDiscountDescription(e.target.value)}
                                    placeholder={t('invoices.discountDescription')}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Totals */}
                    <Card>
                        <CardHeader>
                            <h3 className="text-lg font-semibold">{t('invoices.totals')}</h3>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span>{t('invoices.subtotal')}</span>
                                    <span>CHF {totals.subtotal.toFixed(2)}</span>
                                </div>
                                {totals.discount > 0 && (
                                    <div className="flex justify-between text-rose-600">
                                        <span>
                                            {t('invoices.discount')}{' '}
                                            {discountType === 'percent' ? `(${discountValue}%)` : ''}
                                        </span>
                                        <span>-CHF {totals.discount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span>{t('invoices.tax')}</span>
                                    <span>CHF {totals.taxTotal.toFixed(2)}</span>
                                </div>
                                <hr className="my-2" />
                                <div className="flex justify-between text-lg font-bold">
                                    <span>{t('invoices.total')}</span>
                                    <span>CHF {totals.total.toFixed(2)}</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Actions */}
                    <Card>
                        <CardContent className="pt-6">
                            <div className="space-y-2">
                                <Button
                                    className="w-full"
                                    onClick={() => handleSave(false)}
                                    disabled={saving}
                                >
                                    {saving ? t('invoices.saving') : t('invoices.saveAsDraft')}
                                </Button>
                                <Button
                                    className="w-full"
                                    variant="default"
                                    onClick={() => handleSave(true)}
                                    disabled={saving || lines.filter((l) => !l.isDeleted).length === 0}
                                >
                                    {t('invoices.saveAndSubmit')}
                                </Button>
                                <Button
                                    className="w-full"
                                    variant="outline"
                                    onClick={() => navigate('/invoices')}
                                    disabled={saving}
                                >
                                    {t('invoices.cancel')}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};
